import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Play, Coins, AlertTriangle, CheckCircle2, MapPin, Lock, ShieldCheck, BadgeCheck, XCircle, FlaskConical,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Metric({ label, value, tone, small, testid }) {
  const c = tone === "amber" ? "text-amber-300" : "text-slate-200";
  return (
    <div className="rounded-md border border-border bg-[#0d1016] px-3 py-2" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono ${small ? "text-xs" : "text-lg"} ${c}`}>{value}</div>
    </div>
  );
}

function Row({ k, v, mono = true }) {
  return (
    <div className="flex gap-2 py-0.5 text-[11px]">
      <span className="w-40 shrink-0 text-slate-500">{k}</span>
      <span className={`min-w-0 break-words text-slate-200 ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}

function RequestPreview({ p, testid = "request-preview" }) {
  return (
    <div className="rounded-lg border border-slate-600/40 bg-[#0d1016] p-3" data-testid={testid}>
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">Exact ElevenLabs request (audit)</div>
      <Row k="endpoint" v={p.endpoint} />
      <Row k="voice_id" v={`${p.voice_id} (${p.voice_name})`} />
      <Row k="model_id" v={p.model_id} />
      <Row k="text sent" v={`“${p.text}”`} mono={false} />
      <Row k="pronunciation_dictionary_locators" v={p.pronunciation_dictionary_locators ? JSON.stringify(p.pronunciation_dictionary_locators) : "none"} />
      <Row k="alias / phoneme rules applied" v={p.applied_rules?.length ? JSON.stringify(p.applied_rules) : "none"} />
      <Row k="transformation" v={p.generation_transformation} mono={false} />
      <div className="mt-1.5 flex flex-wrap gap-2 text-[11px]">
        <span className={`rounded px-2 py-0.5 ${p.contains_display_guide ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid={`${testid}-display-flag`}>
          display guide “{p.display_guide}” in payload: {p.contains_display_guide ? "YES ✕" : "no ✓"}
        </span>
        <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300">IPA/phoneme in text: no ✓</span>
      </div>
    </div>
  );
}

export function PronunciationTestBench({ projectId, candidates, onChange }) {
  const drafts = [...candidates].sort((a, b) => {
    const o = { draft: 0, approved: 1, rejected: 2 };
    return (o[a.status] ?? 3) - (o[b.status] ?? 3) || a.written_form.localeCompare(b.written_form);
  });
  const [selId, setSelId] = useState(drafts[0]?.id || "");
  const sel = candidates.find((c) => c.id === selId) || null;

  const [mode, setMode] = useState("sentence");
  const [applyDict, setApplyDict] = useState(false);
  const [occIndex, setOccIndex] = useState(0);
  const [spokenOverride, setSpokenOverride] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ack, setAck] = useState(false);
  const [auditions, setAuditions] = useState([]);
  const [narrator, setNarrator] = useState(null);
  const [dictSynced, setDictSynced] = useState(false);

  useEffect(() => {
    api.get(`/narrator-preset`).then((r) => setNarrator(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/pronunciation-dictionary`).then((r) => setDictSynced(!!r.data.configured)).catch(() => {});
  }, [projectId]);

  useEffect(() => { setOccIndex(0); setSpokenOverride(""); setApplyDict(false); }, [selId]);

  const loadAuditions = useCallback(async () => {
    if (!selId) return;
    try { const { data } = await api.get(`/pronunciations/${selId}/auditions`); setAuditions(data); } catch { /* ignore */ }
  }, [selId]);

  const runPreflight = useCallback(async () => {
    if (!selId) { setPreflight(null); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/pronunciations/${selId}/audition/preflight`, {
        mode, apply_dictionary: applyDict, occurrence_index: occIndex, spoken_form: spokenOverride || undefined,
      });
      setPreflight(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [selId, mode, applyDict, occIndex, spokenOverride]);

  useEffect(() => { runPreflight(); loadAuditions(); }, [runPreflight, loadAuditions]);

  const confirmGenerate = async () => {
    if (!ack) { toast.error("Confirm Scott's locked settings first."); return; }
    if (!window.confirm(`This makes a PAID ElevenLabs call with Scott (~${preflight?.estimated_credits} credits) and generates a single short audition. Continue?`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/pronunciations/${selId}/audition`, {
        mode, apply_dictionary: applyDict, occurrence_index: occIndex, spoken_form: spokenOverride || undefined, confirm: true,
      });
      toast.success(data.cache_hit ? "Replayed a cached audition — no credits used." : "Audition generated with Scott.");
      await loadAuditions(); await runPreflight();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const setVerdict = async (verdict) => {
    try {
      await api.post(`/pronunciations/${selId}/verdict`, { verdict });
      toast.success({ default_verified: "Marked Default Verified.", custom_rule: "Custom rule approved.", rejected: "Rejected." }[verdict]);
      if (onChange) await onChange();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const alts = sel?.alternates || [];

  return (
    <div className="space-y-5" data-testid="test-bench">
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
        <div className="flex items-center gap-2 text-amber-200">
          <FlaskConical className="h-4 w-4" />
          <h2 className="font-serif text-lg">Pronunciation Test Bench</h2>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Runs ONLY the short single-line audition with Scott. No chapter, batch, or production generation.
          Every paid call needs your explicit confirmation; identical requests replay from cache for free.
        </p>
      </div>

      {/* Candidate selector */}
      <div className="rounded-xl border border-border bg-[#12161f] p-4">
        <Label className="text-xs text-slate-400">Draft candidate to test</Label>
        <Select value={selId} onValueChange={setSelId}>
          <SelectTrigger className="mt-1 bg-[#0d1016]" data-testid="testbench-candidate-select"><SelectValue placeholder="Choose a candidate" /></SelectTrigger>
          <SelectContent className="max-h-80">
            {drafts.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.written_form}{c.entry_type === "homograph" ? ` — ${c.sense}` : ""} · {c.status}{c.first_audition_set ? " · first set" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!sel ? (
        <div className="rounded-xl border border-dashed border-border bg-[#12161f]/40 py-16 text-center text-slate-400">Pick a candidate to begin.</div>
      ) : (
        <>
          {/* Scott panel */}
          <div className="rounded-xl border border-border bg-[#12161f] p-4" data-testid="scott-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <span className="font-serif text-base text-slate-100">{narrator?.name || "Scott"}</span>
              </div>
              <span className="flex items-center gap-1 rounded-full bg-slate-800/70 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-300"><Lock className="h-3 w-3" /> Locked</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric label="Model" value={narrator?.model || "—"} small />
              <Metric label="Output" value={narrator?.output || "—"} small />
              <Metric label="Stability" value={narrator?.stability ?? "—"} small />
              <Metric label="Similarity" value={narrator?.similarity ?? "—"} small />
              <Metric label="Style" value={narrator?.style ?? "—"} small />
              <Metric label="Speaker boost" value={String(narrator?.speaker_boost)} small />
              <Metric label="Speed" value={narrator?.speed ?? "—"} small />
              <Metric label="Voice id" value={(narrator?.voice_id || "").slice(0, 8) + "…"} small />
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-slate-300">
              <Switch data-testid="confirm-scott-settings" checked={ack} onCheckedChange={setAck} />
              I confirm Scott and his locked settings for this audition.
            </label>
          </div>

          {/* Controls */}
          <div className="rounded-xl border border-border bg-[#12161f] p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-400">Mode</Label>
                <div className="flex overflow-hidden rounded-md ring-1 ring-border">
                  <button data-testid="testbench-mode-sentence" onClick={() => setMode("sentence")}
                    className={`px-3 py-1.5 text-xs ${mode === "sentence" ? "bg-amber-500/15 text-amber-200" : "text-slate-400"}`}>Manuscript sentence</button>
                  <button data-testid="testbench-mode-isolated" onClick={() => setMode("isolated")}
                    className={`px-3 py-1.5 text-xs ${mode === "isolated" ? "bg-amber-500/15 text-amber-200" : "text-slate-400"}`}>Isolated word</button>
                </div>
              </div>
              <div className="flex items-center gap-2" title={dictSynced ? "" : "Sync an approved dictionary first"}>
                <Switch data-testid="testbench-apply-dictionary" checked={applyDict} disabled={!dictSynced} onCheckedChange={setApplyDict} />
                <Label className="text-xs text-slate-400">Test approved dictionary rule against original spelling {dictSynced ? "" : "(no dictionary synced)"}</Label>
              </div>
            </div>

            {alts.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs text-slate-400">Compare pronunciation</Label>
                <button onClick={() => setSpokenOverride("")}
                  className={`rounded-md px-2 py-1 text-xs ring-1 ${!spokenOverride ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border"}`}>
                  {sel.spoken_form || sel.written_form} (primary)
                </button>
                {alts.map((a, i) => (
                  <button key={i} onClick={() => setSpokenOverride(a.spoken_form)}
                    className={`rounded-md px-2 py-1 text-xs ring-1 ${spokenOverride === a.spoken_form ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border"}`}>
                    {a.spoken_form}{a.label ? ` · ${a.label}` : ""}
                  </button>
                ))}
              </div>
            )}

            {mode === "sentence" && preflight?.occurrence_count > 1 && (
              <div data-testid="testbench-occurrence-selector">
                <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  <MapPin className="h-3 w-3 text-teal-400" /> Target occurrence ({preflight.occurrence_count})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(preflight.occurrences || []).map((o, i) => (
                    <button key={i} data-testid={`testbench-occ-${i}`} onClick={() => setOccIndex(i)} title={o.snippet}
                      className={`max-w-[240px] truncate rounded-md px-2 py-1 text-[11px] ring-1 ${occIndex === i ? "bg-teal-500/15 text-teal-200 ring-teal-500/40" : "text-slate-400 ring-border hover:text-slate-200"}`}>
                      ¶{o.body_number} · {o.snippet}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Preflight */}
          <div className="rounded-xl border border-border bg-[#12161f] p-4">
            {loading || !preflight ? (
              <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Estimating…</div>
            ) : (
              <div className="space-y-3" data-testid="testbench-preflight">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded bg-slate-800/60 px-2 py-0.5 text-slate-300">Display <span className="font-mono text-amber-300">{preflight.display_form || sel.written_form}</span></span>
                  <span className="rounded bg-slate-800/60 px-2 py-0.5 text-slate-300">Generation <span className="font-mono text-teal-300">{preflight.generation_form || preflight.display_form || sel.written_form}</span></span>
                  {preflight.phonetic && <span className="rounded bg-slate-800/60 px-2 py-0.5 text-slate-300">Phonetic <span className="font-mono text-slate-200">/{preflight.phonetic}/</span></span>}
                </div>
                {mode === "sentence" ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-md border border-border bg-[#0d1016] p-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Original manuscript sentence (unchanged)</div>
                      <p className="mt-1 text-sm italic text-slate-400" data-testid="testbench-original">“{preflight.original_sentence}”</p>
                    </div>
                    <div className="rounded-md border border-teal-500/25 bg-teal-500/5 p-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-teal-400/80">{applyDict ? "Original spelling + dictionary → ElevenLabs" : "Generation text → ElevenLabs"}</div>
                      <p className="mt-1 text-sm italic text-slate-100" data-testid="testbench-text">“{preflight.text}”</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Text Scott will speak</div>
                    <p className="mt-1 text-sm italic text-slate-200" data-testid="testbench-text">“{preflight.text}”</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Metric label="Characters" value={preflight.characters} testid="testbench-characters" />
                  <Metric label="Requests" value={preflight.requests} />
                  <Metric label="Est. credits" value={preflight.estimated_credits} tone="amber" testid="testbench-credits" />
                  <Metric label="Output" value={preflight.output_format} small />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span><Coins className="mr-1 inline h-3 w-3 text-amber-400" />{preflight.billing_rate_label}</span>
                  <span>Model <span className="font-mono text-slate-300">{preflight.model}</span></span>
                </div>

                {mode === "isolated" && (
                  <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="isolated-warning">
                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> Isolated single words can be mispronounced without sentence context. Use <b>Manuscript sentence</b> for a reliable test.
                  </div>
                )}

                {preflight.request_preview && <RequestPreview p={preflight.request_preview} />}
                {preflight.cache_hit && (
                  <div className="rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300" data-testid="testbench-cache-hit">
                    <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Identical audition cached — replay is free.
                  </div>
                )}
                {applyDict && !preflight.dictionary_synced && (
                  <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> No dictionary synced. Sync approved rules first.</div>
                )}
                {!preflight.provider_configured && (
                  <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> No ElevenLabs key configured. Add it in Settings.</div>
                )}
                <Button data-testid="testbench-generate" onClick={confirmGenerate} disabled={generating || loading || !ack}
                  className="w-full gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-50">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {preflight.cache_hit ? "Play cached (0 credits)" : "Confirm & Generate (paid)"}
                </Button>
                {!ack && <p className="text-center text-[11px] text-slate-500">Confirm Scott&apos;s locked settings above to enable generation.</p>}
              </div>
            )}
          </div>

          {/* Cached auditions / compare */}
          {auditions.length > 0 && (
            <div className="rounded-xl border border-border bg-[#12161f] p-4">
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Auditions · play & compare</div>
              <div className="space-y-2">
                {auditions.map((a) => (
                  <div key={a.id} className="rounded-lg border border-border bg-[#0d1016] p-3" data-testid={`testbench-audition-${a.id}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-slate-300">{a.label} <span className="text-slate-500">· {a.mode}{a.apply_dictionary ? " · dictionary" : ""}</span></span>
                      <span className="font-mono text-slate-500">{a.char_count} chars · {a.actual_credits ?? a.estimated_credits} cr</span>
                    </div>
                    <audio controls preload="none" className="h-8 w-full" src={`${BACKEND}${a.audio_url}`} data-testid={`testbench-audio-${a.id}`} />
                    {a.request_audit && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-300">Show exact request sent (audit)</summary>
                        <div className="mt-2"><RequestPreview p={a.request_audit} testid={`audit-${a.id}`} /></div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Verdict */}
          <div className="rounded-xl border border-border bg-[#12161f] p-4">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Verdict for “{sel.written_form}”{sel.verdict ? ` · current: ${sel.verdict.replace(/_/g, " ")}` : ""}</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Button data-testid="verdict-default-verified" variant="outline" onClick={() => setVerdict("default_verified")} className="gap-2 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10">
                <BadgeCheck className="h-4 w-4" /> Default Verified
              </Button>
              <Button data-testid="verdict-custom-rule" variant="outline" onClick={() => setVerdict("custom_rule")} className="gap-2 border-teal-500/40 text-teal-200 hover:bg-teal-500/10">
                <CheckCircle2 className="h-4 w-4" /> Custom Rule Approved
              </Button>
              <Button data-testid="verdict-rejected" variant="outline" onClick={() => setVerdict("rejected")} className="gap-2 border-red-500/40 text-red-200 hover:bg-red-500/10">
                <XCircle className="h-4 w-4" /> Rejected
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Default Verified = Scott already reads the original spelling correctly (no rule). Custom Rule Approved = keep this pronunciation (homographs stay generation-only; terms join the dictionary).
            </p>
          </div>
        </>
      )}
    </div>
  );
}
